import React, { useEffect, useMemo, useState } from 'react';
import {
  fetchProcurementProposal,
  ProcurementProposal,
  ProposalCandidate,
  ProposalLine,
  StockAllocation,
} from '../lib/vendorPurchaseOrderApi';
import { formatDate as fmtDate, storedDay } from "../utils/dateFormat";
import { currencyPrefix } from "../utils/priceUtils";
import { roundQty } from "../utils/invoiceLineMath";

/**
 * "Do we already own this?" — reviewed once, before any purchase order is raised.
 *
 * USC buys to order, so inventory only exists as a residual: a vendor minimum
 * forced 120 kg when the vessel wanted 100, and 20 kg stayed behind. This is
 * where a buyer sees those residuals against the order in front of them and
 * decides what to take off the shelf instead of buying again.
 *
 * The buyer confirms; the system never applies a draw on its own. Residual
 * stock is old stock by definition, and only a person can judge whether the
 * batch that has been sitting there three weeks is the right thing to send to a
 * vessel — which is also why every quantity below stays editable.
 */

const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(3).replace(/\.?0+$/, ''));

/**
 * What is left to buy once `draw` comes off the shelf — the client side of the
 * server's `procurementProposal.remainingToBuy`, which PO generation orders:
 * ordered - stock draw - the line's stored pantry draw, rounded ONCE by the
 * quantity owner (`roundQty`), never negative.
 */
const leftToBuy = (line: ProposalLine, draw: number) =>
  Math.max(0, roundQty(line.orderedQuantity - draw - line.qtyFromPantry));

const formatDate = (value?: string | null) => {
  return fmtDate(value, '--');
};

const SEVERITY_STYLE: Record<string, string> = {
  expired: 'bg-red-50 text-red-800 border-red-200',
  short_dated: 'bg-amber-50 text-amber-900 border-amber-200',
  unknown_expiry: 'bg-slate-50 text-slate-700 border-slate-200',
  weak_match: 'bg-amber-50 text-amber-900 border-amber-200',
  uom_not_convertible: 'bg-red-50 text-red-800 border-red-200',
  no_inventory_match: 'bg-slate-50 text-slate-600 border-slate-200',
  no_quantity: 'bg-slate-50 text-slate-600 border-slate-200',
  expired_stock: 'bg-red-50 text-red-800 border-red-200',
};

type Props = {
  confirmedOrderId: string;
  currency?: string | null;
  onCancel: () => void;
  /** Called with the buyer's confirmed draw. An empty array means "buy everything". */
  onConfirm: (allocations: StockAllocation[]) => void | Promise<void>;
};

export const ProcurementProposalModal: React.FC<Props> = ({
  confirmedOrderId,
  currency,
  onCancel,
  onConfirm,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [proposal, setProposal] = useState<ProcurementProposal | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Buyer edits, keyed by line number. Seeded from the proposal, then owned by
  // the buyer — the proposal is a suggestion, not a decision.
  const [draw, setDraw] = useState<Record<number, number>>({});
  const [chosenItem, setChosenItem] = useState<Record<number, string>>({});
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchProcurementProposal(confirmedOrderId);
        if (cancelled) return;
        setProposal(data);
        const seedDraw: Record<number, number> = {};
        const seedItem: Record<number, string> = {};
        for (const line of data.lines) {
          const key = Number(line.lineNumber ?? 0);
          seedDraw[key] = line.proposedFromStock;
          if (line.match) seedItem[key] = line.match.inventoryItemId;
        }
        setDraw(seedDraw);
        setChosenItem(seedItem);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Could not check stock for this order.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [confirmedOrderId]);

  const totals = useMemo(() => {
    if (!proposal) return { lines: 0, saving: 0, covered: 0, unknownSavingLines: 0 };
    let saving = 0;
    let covered = 0;
    let lines = 0;
    let unknownSavingLines = 0;
    for (const line of proposal.lines) {
      const key = Number(line.lineNumber ?? 0);
      const qty = Number(draw[key] || 0);
      if (qty > 0) {
        lines += 1;
        if (line.proposedFromStock > 0 && line.estimatedSaving == null) {
          // Stock exists to draw on, but D54 has not left this line a price yet
          // (no supplier picked — TECH_DEBT #342). Left OUT of the sum below,
          // never folded in as $0, and counted so the bar can say it is missing.
          unknownSavingLines += 1;
        } else {
          const unit = line.proposedFromStock > 0 ? (line.estimatedSaving || 0) / line.proposedFromStock : 0;
          saving += qty * unit;
        }
        if (leftToBuy(line, qty) <= 0) covered += 1;
      }
    }
    return { lines, saving: Math.round(saving * 100) / 100, covered, unknownSavingLines };
  }, [proposal, draw]);

  // The candidate the buyer has picked for a line (D108.2): Take max, the
  // disabled state, layers and shelf-life warnings all belong to IT, not to the
  // server's best match.
  const chosenCandidate = (line: ProposalLine): ProposalCandidate | null => {
    const key = Number(line.lineNumber ?? 0);
    const all = [...(line.match ? [line.match] : []), ...line.alternatives];
    return all.find((c) => c.inventoryItemId === chosenItem[key]) ?? line.match;
  };
  const availableFor = (line: ProposalLine) =>
    chosenCandidate(line)?.availableInOrderUom ?? line.availableInOrderUom;

  const chooseItem = (line: ProposalLine, inventoryItemId: string) => {
    const key = Number(line.lineNumber ?? 0);
    setChosenItem((p) => ({ ...p, [key]: inventoryItemId }));
    const picked = [...(line.match ? [line.match] : []), ...line.alternatives].find(
      (c) => c.inventoryItemId === inventoryItemId
    );
    // Reseed the draw from the picked item's own stock; the old figure was
    // another item's.
    if (picked) {
      setDraw((p) => ({
        ...p,
        [key]: Math.min(picked.suggestedDraw ?? 0, line.orderedQuantity),
      }));
    }
  };

  const setLineQty = (line: ProposalLine, raw: string) => {
    const key = Number(line.lineNumber ?? 0);
    const parsed = Number(raw);
    const max = Math.min(line.orderedQuantity, availableFor(line) || line.orderedQuantity);
    const next = Number.isFinite(parsed) ? Math.max(0, Math.min(parsed, max)) : 0;
    setDraw((prev) => ({ ...prev, [key]: next }));
  };

  const handleConfirm = async () => {
    if (!proposal || submitting) return;
    setSubmitting(true);
    try {
      // Only lines the buyer is actually drawing on are sent. A line left at
      // zero needs no allocation — and sending zeros would release holds the
      // buyer never placed.
      const allocations: StockAllocation[] = proposal.lines
        .map((line) => {
          const key = Number(line.lineNumber ?? 0);
          return {
            lineNumber: key,
            inventoryItemId: chosenItem[key],
            qtyFromStock: Number(draw[key] || 0),
          };
        })
        .filter((a) => a.qtyFromStock > 0 && a.inventoryItemId);

      await onConfirm(allocations);
    } finally {
      setSubmitting(false);
    }
  };

  const linesWithStock = (proposal?.lines || []).filter(
    (l) => l.availableInOrderUom > 0 || l.warnings.length > 0
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[88vh] flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200">
          <h2 className="text-sm font-semibold text-slate-900">Check stock before buying</h2>
          <p className="text-xs text-slate-500 mt-1">
            Goods left over from earlier orders, offered oldest-expiring first. Anything you take
            here is removed from the vendor purchase orders.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && <div className="py-10 text-center text-sm text-slate-500">Checking stock…</div>}

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
              {error}
            </div>
          )}

          {!loading && !error && proposal && linesWithStock.length === 0 && (
            <div className="py-10 text-center text-sm text-slate-500">
              Nothing on this order is already in stock. All {proposal.summary.linesTotal} line(s)
              will be bought from vendors.
            </div>
          )}

          {!loading && !error && proposal && linesWithStock.length > 0 && (
            <div className="flex flex-col gap-3">
              {linesWithStock.map((line) => {
                const key = Number(line.lineNumber ?? 0);
                const qty = Number(draw[key] || 0);
                const toBuy = leftToBuy(line, qty);
                const isOpen = !!expanded[key];
                const chosen = chosenCandidate(line);
                const available = availableFor(line);
                const shownLayers = chosen?.layers ?? line.layers;
                // Layer warnings follow the chosen item; the rest are the line's.
                const shownWarnings = [
                  ...line.warnings.filter((w) => !w.layerId),
                  ...(chosen?.warnings ?? line.warnings.filter((w) => w.layerId)),
                ];

                return (
                  <div key={key} className="border border-slate-200 rounded-md">
                    <div className="px-3 py-2.5 flex flex-wrap items-center gap-3">
                      <div className="min-w-[200px] flex-1">
                        <div className="text-xs font-semibold text-slate-900">
                          {line.lineNumber ? `${line.lineNumber}. ` : ''}
                          {line.itemName || line.itemDescription || 'Item'}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          Ordered {fmt(line.orderedQuantity)} {line.uom}
                          {line.match && (
                            <>
                              {' · matched '}
                              <span className={line.match.confident ? 'text-slate-700' : 'text-amber-800 font-medium'}>
                                {line.match.itemName}
                              </span>
                              {!line.match.confident && ` (${Math.round(line.match.score * 100)}%)`}
                            </>
                          )}
                        </div>
                      </div>

                      <div className="text-xs text-slate-600 whitespace-nowrap">
                        In stock: <span className="font-semibold">{fmt(available)}</span>{' '}
                        {line.uom}
                      </div>

                      <div className="flex items-center gap-1.5">
                        <label className="text-xs text-slate-600">Take</label>
                        <input
                          type="number"
                          min={0}
                          max={Math.min(line.orderedQuantity, available)}
                          step="any"
                          value={qty}
                          onChange={(e) => setLineQty(line, e.target.value)}
                          className="w-20 px-2 py-1 border border-slate-300 rounded text-xs text-right"
                          disabled={!line.match || !(available > 0)}
                        />
                        <span className="text-xs text-slate-500">{line.uom}</span>
                      </div>

                      <div className="text-xs whitespace-nowrap">
                        <span className="text-slate-500">Buy </span>
                        <span className={toBuy === 0 ? 'font-semibold text-emerald-700' : 'font-semibold text-slate-800'}>
                          {fmt(toBuy)} {line.uom}
                        </span>
                      </div>

                      {(shownLayers.length > 0 || line.alternatives.length > 0) && (
                        <button
                          onClick={() => setExpanded((p) => ({ ...p, [key]: !isOpen }))}
                          className="text-xs text-primary-500 font-medium"
                        >
                          {isOpen ? 'Hide' : 'Details'}
                        </button>
                      )}
                    </div>

                    {shownWarnings.length > 0 && (
                      <div className="px-3 pb-2 flex flex-col gap-1">
                        {shownWarnings.map((w, i) => (
                          <div
                            key={i}
                            className={`text-xs px-2 py-1 rounded border ${
                              SEVERITY_STYLE[w.code] || 'bg-slate-50 text-slate-600 border-slate-200'
                            }`}
                          >
                            {w.message}
                          </div>
                        ))}
                      </div>
                    )}

                    {isOpen && (
                      <div className="px-3 pb-3 border-t border-slate-100 pt-2">
                        {line.alternatives.length > 0 && (
                          <div className="mb-3">
                            <div className="text-xs font-semibold text-slate-700 mb-1">
                              Which product is this?
                            </div>
                            <select
                              value={chosenItem[key] || ''}
                              onChange={(e) => chooseItem(line, e.target.value)}
                              className="w-full px-2 py-1 border border-slate-300 rounded text-xs"
                            >
                              {[...(line.match ? [line.match] : []), ...line.alternatives].map((c) => (
                                <option key={c.inventoryItemId} value={c.inventoryItemId}>
                                  {c.itemName} ({Math.round(c.score * 100)}% match
                                  {c.availableInOrderUom != null
                                    ? `, ${fmt(c.availableInOrderUom)} ${line.uom} in stock`
                                    : ''}
                                  )
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                        {shownLayers.length > 0 && (
                          <>
                            <div className="text-xs font-semibold text-slate-700 mb-1">
                              Which stock would be used
                            </div>
                            <div className="overflow-x-auto">
                              <table className="data-table data-table-dense">
                                <thead>
                                  <tr className="text-slate-500 text-left">
                                    <th className="py-1 pr-3 font-semibold">Qty</th>
                                    <th className="py-1 pr-3 font-semibold">Expires</th>
                                    <th className="py-1 pr-3 font-semibold">Received</th>
                                    <th className="py-1 pr-3 font-semibold">From invoice</th>
                                    <th className="py-1 pr-3 font-semibold">Bought for</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {shownLayers.map((layer) => (
                                    <tr key={layer.layerId} className="border-t border-slate-100">
                                      <td className="py-1 pr-3">
                                        {fmt(layer.quantityInOrderUom)} {line.uom}
                                      </td>
                                      <td className="py-1 pr-3">
                                        {fmtDate(storedDay(layer.expiryDate), '--')}
                                        {layer.remainingDays != null && (
                                          <span
                                            className={
                                              layer.remainingDays < 30
                                                ? 'text-amber-700 ml-1'
                                                : 'text-slate-400 ml-1'
                                            }
                                          >
                                            ({layer.remainingDays}d)
                                          </span>
                                        )}
                                      </td>
                                      <td className="py-1 pr-3 text-slate-500">
                                        {formatDate(layer.receivedAt)}
                                      </td>
                                      <td className="py-1 pr-3 text-slate-500">
                                        {layer.sourceInvoiceNumber || '--'}
                                        {layer.sourceVendorName ? ` · ${layer.sourceVendorName}` : ''}
                                      </td>
                                      <td className="py-1 pr-3 text-slate-500">
                                        {layer.originalVesselName || '--'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between gap-3">
          <div className="text-xs text-slate-600">
            {totals.lines > 0 ? (
              <>
                Drawing on <span className="font-semibold">{totals.lines}</span> line(s)
                {totals.covered > 0 && <> · {totals.covered} needs no vendor PO</>}
                {totals.saving > 0 && (
                  <>
                    {' '}
                    · avoids buying{' '}
                    <span className="font-semibold">
                      {currencyPrefix(currency)}
                      {totals.saving.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  </>
                )}
                {totals.unknownSavingLines > 0 && (
                  <>
                    {' '}
                    · saving pending for{' '}
                    <span className="font-semibold text-amber-700">{totals.unknownSavingLines}</span>{' '}
                    line{totals.unknownSavingLines === 1 ? '' : 's'} (no supplier price yet)
                  </>
                )}
              </>
            ) : (
              'Nothing selected — every line will be bought from vendors.'
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onCancel}
              disabled={submitting}
              className="px-3 py-2 rounded-md border border-slate-200 bg-white text-xs font-semibold text-slate-700 disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={submitting || loading}
              className="px-3 py-2 rounded-md bg-primary-500 text-white text-xs font-semibold disabled:opacity-60"
            >
              {submitting ? 'Working…' : 'Confirm and generate POs'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcurementProposalModal;
